import Lenis from "lenis";
import { LenisCallback } from "../types/Callback";
import { getRootPropertyValue } from "../utils.common/getRootPropertyValue";
import { resizeService } from "./resizeService";
// import gsap from "gsap";
// import { ScrollTrigger } from "gsap/ScrollTrigger";

class ScrollService {
	wrapper: HTMLElement | Window;
	content: HTMLElement;
	lenis!: Lenis;
	lastScrollPos = 0;
	callbackArr: LenisCallback[] = [];
	#headerHeight = 0;

	constructor() {
		this.wrapper =
			(document.querySelector("[data-scroll-wrapper]") as HTMLElement) ||
			(window as Window);
		this.content =
			document.querySelector("[data-scroll-content]") ||
			document.documentElement;

		this.#initLenis();
	}

	#handleAnchorClick = (e: Event) => {
		const target = e.target as HTMLElement;
		const anchor = target.closest(`a[href*="#"]`) as HTMLAnchorElement;
		if (
			!anchor ||
			anchor.origin !== window.location.origin ||
			anchor.pathname !== window.location.pathname ||
			anchor.matches("[data-lenis-ignore]")
		) {
			return;
		}

		e.preventDefault();

		const element = document.querySelector(anchor.hash) as HTMLElement;

		const scrollMargin = element
			? parseInt(window.getComputedStyle(element).scrollMarginTop)
			: 0;

		this.lenis.scrollTo(
			(element?.matches(".anchor") ? element.parentElement : element) ||
				0,
			{
				offset: -(
					scrollMargin || getRootPropertyValue("--header-height")
				),
				duration: 1,
				force: true,
				onComplete: () => {
					const anchorScrollEvent = new Event("anchorScroll", {
						bubbles: true,
					});
					this.wrapper.dispatchEvent(anchorScrollEvent);
				},
			},
		);

		if (element) {
			history.pushState({}, "", anchor.hash);
		}
	};

	#initLenis = (): void => {
		const supportsBaseSelect = CSS?.supports("appearance", "base-select");

		this.lenis = new Lenis({
			wrapper: this.wrapper,
			content: this.content,
			allowNestedScroll: true,
			prevent: (node) => {
				if (supportsBaseSelect) {
					const select = node.closest(
						"select:open",
					) as HTMLSelectElement;
					if (select) {
						const pickerStyles = window.getComputedStyle(
							select,
							"::picker(select)",
						);
						const isHovered =
							pickerStyles.getPropertyValue(
								"--picker-hovered",
							) === "1";
						if (isHovered) {
							const options = select.options;
							if (options.length === 0) return false;

							const height = parseFloat(pickerStyles.height) || 0;
							const paddingTop =
								parseFloat(pickerStyles.paddingTop) || 0;
							const paddingBottom =
								parseFloat(pickerStyles.paddingBottom) || 0;
							const borderTop =
								parseFloat(pickerStyles.borderTopWidth) || 0;
							const borderBottom =
								parseFloat(pickerStyles.borderBottomWidth) || 0;
							const availableScrollSpace =
								height -
								paddingTop -
								paddingBottom -
								borderTop -
								borderBottom;

							const firstChild = options[0];
							const lastChild = options[options.length - 1];
							const firstChildRect =
								firstChild.getBoundingClientRect();
							const lastChildRect =
								lastChild.getBoundingClientRect();
							const firstStyle =
								window.getComputedStyle(firstChild);
							const lastStyle =
								window.getComputedStyle(lastChild);
							const marginTop =
								parseFloat(firstStyle.marginTop) || 0;
							const marginBottom =
								parseFloat(lastStyle.marginBottom) || 0;

							const contentHeight =
								lastChildRect.bottom -
								firstChildRect.top +
								marginTop +
								marginBottom;

							return contentHeight > availableScrollSpace;
						}
					}
				}
				return false;
			},
		});

		// comment out following 5 lines when using gsap
		const raf = (time: number): void => {
			this.lenis.raf(time);
			requestAnimationFrame(raf);
		};
		requestAnimationFrame(raf);

		// gsap.registerPlugin(ScrollTrigger);
		// this.lenis.on("scroll", ScrollTrigger.update);
		// gsap.ticker.add((time) => {
		// 	this.lenis.raf(time * 1000);
		// });

		this.#calculateHeaderHeight();
		resizeService.add(this.#calculateHeaderHeight);
		this.setScrolledClass(document.documentElement.scrollTop);

		this.lenis.on("scroll", (l) => {
			this.setScrolledClass(l.actualScroll);
			this.callbackArr.forEach((c) => c(l));
			this.lastScrollPos = l.animatedScroll;
		});

		document.addEventListener("click", this.#handleAnchorClick);
	};

	setScrolledClass = (scroll: number) => {
		document.documentElement.classList.toggle(
			"scrolled",
			scroll > this.#headerHeight,
		);
	};

	#calculateHeaderHeight = () => {
		this.#headerHeight = getRootPropertyValue(
			"--header-height",
			"rem",
		) as number;
	};

	add = (callback: LenisCallback) => {
		this.callbackArr.push(callback);
	};

	remove = (callback: LenisCallback) => {
		this.callbackArr = this.callbackArr.filter((c) => c != callback);
	};

	disableBodyScroll = (): void => {
		if (this.lenis.isStopped) {
			return;
		}
		this.lenis.stop();
	};

	enableBodyScroll = (): void => {
		this.lenis.start();
	};

	reinit = (): void => {
		this.lenis.start();
	};
}

export const scrollService = new ScrollService();
